import { useState, useEffect } from 'react';
import {
    Box,
    Card,
    Grid,
    Stack,
    Typography,
    CircularProgress,
    CardHeader,
    useTheme,
} from '@mui/material';
import Chart from 'react-apexcharts';
import Iconify from '../../../components/iconify';
import { reportingService } from '../../../api/reporting.service';

// ----------------------------------------------------------------------

type DashboardStats = {
    activePatients: number;
    recallsDue: number;
    appointmentsThisMonth: number;
    salesToday: number;
    examsToday: number;
    totalStockValue: number;
    salesTrends: { month: string, total: number }[];
    nhsClaimsStatus: { label: string, value: number }[];
};

export default function DashboardWidgets() {
    const theme = useTheme();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const data = await reportingService.getDashboardStats();
                setStats(data);
            } catch (error) {
                console.error('Failed to fetch dashboard stats:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!stats) return null;

    const salesChartOptions = {
        chart: { toolbar: { show: false } },
        stroke: { curve: 'smooth' as const },
        xaxis: { categories: stats.salesTrends.map(s => s.month) },
        tooltip: { y: { formatter: (val: number) => `£${val.toLocaleString()}` } },
        colors: [theme.palette.primary.main],
    };

    const nhsChartOptions = {
        labels: stats.nhsClaimsStatus.map(s => s.label),
        colors: [theme.palette.success.main, theme.palette.warning.main, theme.palette.error.main],
        legend: { position: 'bottom' },
        plotOptions: { pie: { donut: { size: '75%' } } },
    } as any;

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
             
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        title="Sales Today"
                        total={stats.salesToday}
                        icon="eva:shopping-cart-fill"
                        color="success"
                        prefix="£"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        title="Exams Completed (Today)"
                        total={stats.examsToday}
                        icon="eva:file-text-fill"
                        color="info"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        title="Recalls Due (This Month)"
                        total={stats.recallsDue}
                        icon="eva:bell-fill"
                        color="warning"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        title="Stock Value"
                        total={stats.totalStockValue}
                        icon="eva:cube-fill"
                        color="primary"
                        prefix="£"
                    />
                </Grid>

                {/* Row 2: Secondary Metrics */}
                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        title="Active Patients"
                        total={stats.activePatients}
                        icon="eva:people-fill"
                        color="primary"
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <SummaryCard
                        title="Monthly Appointments"
                        total={stats.appointmentsThisMonth}
                        icon="eva:calendar-fill"
                        color="info"
                    />
                </Grid>

                {/* Charts */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardHeader title="Sales Trends (Last 6 Months)" />
                        <Box sx={{ p: 3, pb: 1 }}>
                            <Chart
                                type="line"
                                series={[{ name: 'Revenue', data: stats.salesTrends.map(s => s.total) }]}
                                options={salesChartOptions}
                                height={320}
                            />
                        </Box>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card>
                        <CardHeader title="NHS Claims Status" />
                        <Box sx={{ p: 3, pb: 1, display: 'flex', justifyContent: 'center' }}>
                            <Chart
                                type="donut"
                                series={stats.nhsClaimsStatus.map(s => s.value)}
                                options={nhsChartOptions}
                                height={345}
                            />
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}

// ----------------------------------------------------------------------

type SummaryCardProps = {
    title: string;
    total: number;
    icon: string;
    color: string;
    prefix?: string;
    suffix?: string;
};

function SummaryCard({ title, total, icon, color, prefix = '', suffix = '' }: SummaryCardProps) {
    return (
        <Card sx={{ p: 3, textAlign: 'center' }}>
            <Stack spacing={1}>
                <Box
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: (theme: any) => theme.palette[color].lighter,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: (theme: any) => theme.palette[color].dark,
                        mx: 'auto',
                        mb: 1,
                    }}
                >
                    <Iconify icon={icon} width={24} height={24} />
                </Box>
                <Typography variant="h3">
                    {prefix}{total.toLocaleString()}{suffix}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    {title}
                </Typography>
            </Stack>
        </Card>
    );
}
